import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import BottomNavigation from './components/BottomNavigation';
import DashboardContent from './components/DashboardContent';
import BroadcastContent from './components/BroadcastContent';
import LoginForm from './components/LoginForm';
import Swal from 'sweetalert2';

interface Subdomain {
  id: string;
  subdomain: string;
  domain: string;
  directory: string;
  status: string;
  created_at: string;
}

interface Directory {
  path: string;
  label: string;
}

interface Domain {
  domain: string;
  type: string;
  status: string;
}

function App() {
  const [currentView, setCurrentView] = useState<'login' | 'dashboard' | 'broadcast'>('login');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [showOtpSection, setShowOtpSection] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [userPhone, setUserPhone] = useState('');
  const [subdomains, setSubdomains] = useState<Subdomain[]>([]);
  const [directories, setDirectories] = useState<Directory[]>([]);
  const [availableDomains, setAvailableDomains] = useState<Domain[]>([]);
  const [activeDomains, setActiveDomains] = useState<Subdomain[]>([]);
  const [allSubdomains, setAllSubdomains] = useState<Subdomain[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [monitoringActive, setMonitoringActive] = useState(false);
  const [monitoringInterval, setMonitoringInterval] = useState<NodeJS.Timeout | null>(null);


  // Form states
  const [subdomainName, setSubdomainName] = useState('');
  const [domainName, setDomainName] = useState('');
  const [directoryPath, setDirectoryPath] = useState('');
  const [targetDomain, setTargetDomain] = useState('');
  const [targetPhone, setTargetPhone] = useState('');
  const [broadcastMessage] = useState('Ini adalah alamat modal Livina Gemilang Plastik yang baru');

  const apiCall = async (endpoint: string, method: string = 'GET', data?: any) => {
    // Use PHP development server on port 8080
    const baseUrl = 'https://manager.mdal.my.id/api';
    const url = `${baseUrl}${endpoint}`;
    
    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('session_token') || ''}`
      }
    };
    
    if (data && method !== 'GET') {
      options.body = JSON.stringify(data);
    }
    
    const response = await fetch(url, options);
    return response.json();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await apiCall('/auth/request-otp', 'POST', { phone_number: phoneNumber });
      
      if (result.success) {
        setShowOtpSection(true);
        Swal.fire({
          icon: 'success',
          title: 'Berhasil!',
          text: 'Kode OTP berhasil dikirim ke nomor WhatsApp Anda!',
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Gagal Mengirim OTP',
          text: result.message || 'Terjadi kesalahan saat mengirim OTP',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      Swal.fire({
        icon: 'error',
        title: 'Kesalahan Jaringan',
        text: 'Terjadi kesalahan saat mengirim OTP. Periksa koneksi internet Anda.',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await apiCall('/auth/verify-otp', 'POST', {
        phone_number: phoneNumber,
        otp_code: otpCode
      });
      
      if (result.success) {
        localStorage.setItem('session_token', result.token);
        localStorage.setItem('user_phone', phoneNumber);
        setUserPhone(phoneNumber);
        setCurrentView('dashboard');
        loadSubdomains();
        loadDirectories();
        loadDomains();
        Swal.fire({
          icon: 'success',
          title: 'Login Berhasil!',
          text: 'Selamat datang di Dashboard cPanel',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Verifikasi Gagal',
          text: `Kode OTP tidak valid: ${result.message}`,
          confirmButtonText: 'Coba Lagi'
        });
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      Swal.fire({
        icon: 'error',
        title: 'Kesalahan Jaringan',
        text: 'Terjadi kesalahan saat verifikasi OTP. Periksa koneksi internet Anda.',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadSubdomains = async () => {
    try {
      const result = await apiCall('/subdomains');
      if (result.success) {
        // Map API response to component interface
        const mappedSubdomains = (result.subdomains || []).map((sub: any) => ({
          id: sub.id.toString(),
          subdomain: sub.subdomain_name,
          domain: sub.domain,
          directory: sub.directory_path,
          status: sub.status || 'active',
          created_at: sub.created_at
        }));
        setSubdomains(mappedSubdomains);
      }
    } catch (error) {
      console.error('Error loading subdomains:', error);
    }
  };

  const loadAllSubdomains = async () => {
    try {
      const result = await apiCall('/subdomains/history');
      if (result.success) {
        // Map API response to component interface
        const mappedSubdomains = (result.subdomains || []).map((sub: any) => ({
          id: sub.id.toString(),
          subdomain: sub.subdomain_name,
          domain: sub.domain,
          directory: sub.directory_path,
          status: sub.status || 'active',
          created_at: sub.created_at
        }));
        setAllSubdomains(mappedSubdomains);
      }
    } catch (error) {
      console.error('Error loading all subdomains:', error);
    }
  };

  const loadDirectories = async () => {
    try {
      const result = await apiCall('/subdomains/directories');
      if (result.success) {
        // Map API response to component interface
        const mappedDirectories = (result.directories || []).map((dir: any) => ({
          path: dir.path,
          label: dir.name
        }));
        setDirectories(mappedDirectories);
        // Auto-select Modal Gemilang directory if available
        const modalGemilangDir = mappedDirectories.find((dir: Directory) => 
          dir.path === '/public_html/modalgemilang'
        );
        if (modalGemilangDir) {
          setDirectoryPath(modalGemilangDir.path);
        }
      }
    } catch (error) {
      console.error('Error loading directories:', error);
    }
  };

  const loadDomains = async () => {
    try {
      const result = await apiCall('/subdomains/domains');
      if (result.success) {
        // Map API response to component interface
        const mappedDomains = (result.domains || []).map((domain: any) => ({
          domain: domain.domain,
          type: domain.type || 'main',
          status: domain.status || 'active'
        }));
        setAvailableDomains(mappedDomains);
        // Auto-select first domain from array
        if (mappedDomains.length > 0) {
          setDomainName(mappedDomains[0].domain);
        }
      }
    } catch (error) {
      console.error('Error loading domains:', error);
    }
  };

  const loadActiveDomains = async () => {
    try {
      const result = await apiCall('/subdomains');
      if (result.success) {
        // Map API response and filter active domains
        const mappedSubdomains = (result.subdomains || []).map((sub: any) => ({
          id: sub.id.toString(),
          subdomain: sub.subdomain_name,
          domain: sub.domain,
          directory: sub.directory_path,
          status: 'active', // Default status since API doesn't provide it
          created_at: sub.created_at
        }));
        setActiveDomains(mappedSubdomains);
      }
    } catch (error) {
      console.error('Error loading active domains:', error);
    }
  };

  const handleCreateSubdomain = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Create subdomain via API
      const result = await apiCall('/subdomains', 'POST', {
        subdomain_name: subdomainName,
        domain: domainName,
        directory_path: directoryPath
      });
      
      if (result.success) {
        const fullDomain = `${subdomainName}.${domainName}`;
        Swal.fire({
          icon: 'success',
          title: 'Subdomain Berhasil Dibuat!',
          text: `Subdomain ${fullDomain} telah berhasil dibuat. Menunggu 3 detik untuk propagasi DNS...`,
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false
        });
        
        // Wait 3 seconds for DNS propagation
        setTimeout(async () => {
          try {
            // Use backend API with cURL to check subdomain response
             const checkResult = await apiCall(`/subdomains/check_response?subdomain=${subdomainName}&domain=${domainName}`);
             
             if (checkResult.status === true) {
               Swal.fire({
                 icon: 'success',
                 title: 'Subdomain Dapat Diakses!',
                 html: `<strong>✅ ${fullDomain}</strong> berhasil dibuat dan dapat diakses!<br><br><strong>Kode HTTP:</strong> ${checkResult.http_code}<br><strong>Response:</strong><br><pre style="text-align: left; font-size: 12px;">${JSON.stringify(checkResult.response, null, 2)}</pre><br><strong>URL:</strong> <a href="http://${fullDomain}" target="_blank">http://${fullDomain}</a>`,
                 confirmButtonText: 'OK'
               });
             } else {
               // Show actual response even if status is false
               const responseText = checkResult.response ? JSON.stringify(checkResult.response, null, 2) : 'No response';
               Swal.fire({
                 icon: 'warning',
                 title: 'Subdomain Belum Dapat Diakses',
                 html: `<strong>⚠️ ${fullDomain}</strong> berhasil dibuat tapi belum dapat diakses.<br><br><strong>Kode HTTP:</strong> ${checkResult.http_code}<br><strong>Response:</strong><br><pre style="text-align: left; font-size: 12px;">${responseText}</pre><br><strong>Error:</strong> ${checkResult.error || 'N/A'}<br><strong>URL:</strong> <a href="http://${fullDomain}" target="_blank">http://${fullDomain}</a>`,
                 confirmButtonText: 'OK'
               });
             }
          } catch (error: any) {
            Swal.fire({
              icon: 'error',
              title: 'Gagal Memeriksa Response',
              text: `Subdomain ${fullDomain} berhasil dibuat tapi terjadi error saat pengecekan: ${error.message}`,
              confirmButtonText: 'OK'
            });
          }
        }, 3000);
        
        setSubdomainName('');
        setDomainName('');
        setShowCreateModal(false);
        loadSubdomains();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Gagal Membuat Subdomain',
          text: result.message || 'Terjadi kesalahan saat membuat subdomain',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error creating subdomain:', error);
      Swal.fire({
        icon: 'error',
        title: 'Kesalahan Jaringan',
        text: 'Terjadi kesalahan saat membuat subdomain. Periksa koneksi internet Anda.',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSubdomain = async (id: string) => {
    const result = await Swal.fire({
      title: 'Konfirmasi Hapus Subdomain',
      text: 'Apakah Anda yakin ingin menghapus subdomain ini?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ya, Hapus!',
      cancelButtonText: 'Batal'
    });
    
    if (!result.isConfirmed) {
      return;
    }
    
    try {
      const result = await apiCall(`/subdomains/${id}`, 'DELETE');
      
      if (result.success) {
        Swal.fire({
          icon: 'success',
          title: 'Berhasil!',
          text: 'Subdomain berhasil dihapus!',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false
        });
        // Reload both active and all subdomains to keep data consistent
        loadSubdomains();
        if (showHistory) {
          loadAllSubdomains();
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Gagal Menghapus',
          text: result.message || 'Gagal menghapus subdomain',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Kesalahan Jaringan',
        text: 'Terjadi kesalahan saat menghapus subdomain. Periksa koneksi internet Anda.',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleBroadcast = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await apiCall('/broadcast', 'POST', {
        phone_number: targetPhone,
        domain: targetDomain
      });
      
      if (result.success) {
        Swal.fire({
          icon: 'success',
          title: 'Berhasil!',
          text: 'Link berhasil dikirim ke WhatsApp!',
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false
        });
        setTargetPhone('');
        setTargetDomain('');
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Gagal Mengirim',
          text: result.message || 'Gagal mengirim link',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Kesalahan Jaringan',
        text: 'Terjadi kesalahan saat mengirim link. Periksa koneksi internet Anda.',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBroadcastMultiple = async (phoneNumbers: string[]) => {
    setIsLoading(true);
    
    try {
      const result = await apiCall('/broadcast', 'POST', {
        phone_numbers: phoneNumbers,
        domain: targetDomain
      });
      
      if (result.success) {
        const successCount = result.results?.filter((r: any) => r.status === 'sent').length || 0;
        const failedCount = result.results?.filter((r: any) => r.status === 'failed').length || 0;
        
        Swal.fire({
          icon: successCount > 0 ? 'success' : 'error',
          title: 'Broadcast Selesai',
          html: `<strong>Berhasil:</strong> ${successCount} pesan<br><strong>Gagal:</strong> ${failedCount} pesan`,
          timer: 4000,
          timerProgressBar: true,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Gagal Broadcast',
          text: result.message || 'Gagal mengirim broadcast',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Kesalahan Jaringan',
        text: 'Terjadi kesalahan saat mengirim broadcast. Periksa koneksi internet Anda.',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckSubdomainResponse = async (subdomain: string, domain: string) => {
    try {
      const fullDomain = `${subdomain}.${domain}`;
      
      // Use backend API with cURL to check subdomain response
       const result = await apiCall(`/subdomains/check_response?subdomain=${subdomain}&domain=${domain}`);
       
       if (result.status === true) {
         Swal.fire({
           icon: 'success',
           title: 'Subdomain Dapat Diakses!',
           html: `<strong>✅ ${fullDomain}</strong> dapat diakses dan merespons dengan benar!<br><br><strong>Kode HTTP:</strong> ${result.http_code}<br><strong>Response:</strong><br><pre style="text-align: left; font-size: 12px;">${JSON.stringify(result.response, null, 2)}</pre><br><strong>URL:</strong> <a href="http://${fullDomain}" target="_blank">http://${fullDomain}</a>`,
           confirmButtonText: 'OK'
         });
       } else {
         // Show actual response even if status is false
         const responseText = result.response ? JSON.stringify(result.response, null, 2) : 'Tidak ada response';
         Swal.fire({
           icon: 'error',
           title: 'Subdomain Tidak Dapat Diakses',
           html: `<strong>❌ ${fullDomain}</strong> tidak dapat diakses atau response tidak valid.<br><br><strong>Kode HTTP:</strong> ${result.http_code}<br><strong>Response:</strong><br><pre style="text-align: left; font-size: 12px;">${responseText}</pre><br><strong>Error:</strong> ${result.error || 'Kesalahan tidak diketahui'}<br><strong>URL:</strong> <a href="http://${fullDomain}" target="_blank">http://${fullDomain}</a>`,
           confirmButtonText: 'OK'
         });
       }
    } catch (error: any) {
      const fullDomain = `${subdomain}.${domain}`;
      Swal.fire({
        icon: 'error',
        title: 'Kesalahan Jaringan',
        text: `Terjadi kesalahan saat mengecek subdomain ${fullDomain}: ${error.message}`,
        confirmButtonText: 'OK'
      });
    }
  };

  const handleToggleHistory = () => {
    setShowHistory(!showHistory);
    if (!showHistory) {
      // Switching to history view, load all subdomains
      loadAllSubdomains();
    } else {
      // Switching back to active view, reload active subdomains
      loadSubdomains();
    }
  };

  const handleToggleMonitoring = () => {
     if (monitoringActive) {
       stopMonitoring();
       Swal.fire({
         icon: 'info',
         title: 'Monitoring Dimatikan',
         text: 'Monitoring otomatis subdomain telah dimatikan',
         timer: 2000,
         timerProgressBar: true,
         showConfirmButton: false
       });
     } else {
       startMonitoring();
       Swal.fire({
         icon: 'success',
         title: 'Monitoring Diaktifkan',
         text: 'Monitoring otomatis subdomain setiap 5 detik telah diaktifkan',
         timer: 2000,
         timerProgressBar: true,
         showConfirmButton: false
       });
     }
   };

   const handleLogout = async () => {
     const result = await Swal.fire({
       title: 'Konfirmasi Logout',
       text: 'Apakah Anda yakin ingin keluar dari aplikasi?',
       icon: 'question',
       showCancelButton: true,
       confirmButtonColor: '#3085d6',
       cancelButtonColor: '#d33',
       confirmButtonText: 'Ya, Keluar',
       cancelButtonText: 'Batal'
     });
     
     if (result.isConfirmed) {
       // Stop monitoring when logging out
       if (monitoringActive) {
         stopMonitoring();
       }
       
       localStorage.removeItem('session_token');
       localStorage.removeItem('user_phone');
       setCurrentView('login');
       setUserPhone('');
       setPhoneNumber('');
       setOtpCode('');
       setShowOtpSection(false);
       
       Swal.fire({
         icon: 'success',
         title: 'Logout Berhasil',
         text: 'Anda telah berhasil keluar dari aplikasi',
         timer: 2000,
         timerProgressBar: true,
         showConfirmButton: false
       });
     }
   };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'active':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'inactive':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'gangguan':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  // Check authentication on app load
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('session_token');
      const savedPhone = localStorage.getItem('user_phone');
      
      if (token && savedPhone) {
        try {
          const result = await apiCall('/auth/validate');
          if (result.success) {
            setUserPhone(savedPhone);
            setCurrentView('dashboard');
            loadSubdomains();
            loadDirectories();
            loadDomains();
          } else {
            // Invalid token, clear storage and show login
            localStorage.removeItem('session_token');
            localStorage.removeItem('user_phone');
            setCurrentView('login');
          }
        } catch (error) {
          // API error, clear storage and show login
          localStorage.removeItem('session_token');
          localStorage.removeItem('user_phone');
          setCurrentView('login');
        }
      } else {
        setCurrentView('login');
      }
    };
    
    checkAuth();
  }, []);

  // Monitoring function
  const startMonitoring = () => {
    if (monitoringInterval) {
      clearInterval(monitoringInterval);
    }
    
    setMonitoringActive(true);
    const interval = setInterval(async () => {
       if (subdomains.length > 0) {
         const updatedSubdomains = [...subdomains];
         let hasChanges = false;
         
         for (let i = 0; i < updatedSubdomains.length; i++) {
           const subdomain = updatedSubdomains[i];
           try {
             const checkResult = await apiCall(`/subdomains/check_response?subdomain=${subdomain.subdomain}&domain=${subdomain.domain}`, 'GET');
             
             if (checkResult.status !== true) {
               // Update status to 'gangguan' if not already
               if (subdomain.status !== 'gangguan') {
                 updatedSubdomains[i] = { ...subdomain, status: 'gangguan' };
                 hasChanges = true;
                 
                 Swal.fire({
                   icon: 'warning',
                   title: 'Link Sedang Gangguan',
                   text: `Subdomain ${subdomain.subdomain}.${subdomain.domain} tidak dapat diakses dan telah diblokir.`,
                   toast: true,
                   position: 'top-end',
                   showConfirmButton: false,
                   timer: 4000,
                   timerProgressBar: true
                 });
               }
             } else {
               // Update status back to 'active' if it was 'gangguan'
               if (subdomain.status === 'gangguan') {
                 updatedSubdomains[i] = { ...subdomain, status: 'active' };
                 hasChanges = true;
                 
                 Swal.fire({
                   icon: 'success',
                   title: 'Link Sudah Normal',
                   text: `Subdomain ${subdomain.subdomain}.${subdomain.domain} sudah dapat diakses kembali.`,
                   toast: true,
                   position: 'top-end',
                   showConfirmButton: false,
                   timer: 3000,
                   timerProgressBar: true
                 });
               }
             }
           } catch (error) {
             console.error('Monitoring error:', error);
           }
         }
         
         // Update state if there are changes
         if (hasChanges) {
           setSubdomains(updatedSubdomains);
         }
       }
     }, 5000); // Check every 5 seconds
    
    setMonitoringInterval(interval);
  };

  const stopMonitoring = () => {
    if (monitoringInterval) {
      clearInterval(monitoringInterval);
      setMonitoringInterval(null);
    }
    setMonitoringActive(false);
  };

  // Cleanup monitoring on component unmount
  useEffect(() => {
    return () => {
      if (monitoringInterval) {
        clearInterval(monitoringInterval);
      }
    };
  }, [monitoringInterval]);

  if (currentView === 'login') {
    return (
      <LoginForm
        phoneNumber={phoneNumber}
        otpCode={otpCode}
        showOtpSection={showOtpSection}
        isLoading={isLoading}
        onLogin={handleLogin}
        onOTPVerification={handleOTPVerification}
        onPhoneNumberChange={setPhoneNumber}
        onOtpCodeChange={setOtpCode}
        onBackToPhone={() => {
          setShowOtpSection(false);
          setOtpCode('');
        }}
      />
    );
  }

  if (currentView === 'broadcast') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar 
          userPhone={userPhone}
          currentView={currentView}
          onLogout={handleLogout}
        />
        
        <BroadcastContent
          activeDomains={activeDomains}
          targetDomain={targetDomain}
          targetPhone={targetPhone}
          broadcastMessage={broadcastMessage}
          isLoading={isLoading}
          onBroadcast={handleBroadcast}
          onBroadcastMultiple={handleBroadcastMultiple}
          onTargetDomainChange={setTargetDomain}
          onTargetPhoneChange={setTargetPhone}
          apiCall={apiCall}
        />

        <BottomNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          onBroadcastClick={() => {
            setCurrentView('broadcast');
            loadActiveDomains();
          }}
        />
      </div>
    );
  }

  // Dashboard View
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar 
        userPhone={userPhone}
        currentView={currentView}
        onLogout={handleLogout}
      />
      
      <DashboardContent
        subdomains={showHistory ? allSubdomains : subdomains}
        directories={directories}
        availableDomains={availableDomains}
        showCreateModal={showCreateModal}
        isLoading={isLoading}
        subdomainName={subdomainName}
        domainName={domainName}
        directoryPath={directoryPath}
        showHistory={showHistory}
        monitoringActive={monitoringActive}
        onCreateSubdomain={handleCreateSubdomain}
        onDeleteSubdomain={handleDeleteSubdomain}
        onCheckResponse={handleCheckSubdomainResponse}
        onRefresh={showHistory ? loadAllSubdomains : loadSubdomains}
        onShowCreateModal={setShowCreateModal}
        onSubdomainNameChange={setSubdomainName}
        onDomainNameChange={setDomainName}
        onDirectoryPathChange={setDirectoryPath}
        onToggleHistory={handleToggleHistory}
        onToggleMonitoring={handleToggleMonitoring}
      />

      <BottomNavigation
        currentView={currentView}
        onNavigate={setCurrentView}
        onBroadcastClick={() => {
          setCurrentView('broadcast');
          loadActiveDomains();
        }}
      />
    </div>
  );
}

export default App;
